import { cx } from '@emotion/css';
import type { FC, PropsWithChildren } from 'react';
import { Children, isValidElement, useRef } from 'react';

import { Alignment } from '../../constants';
import { useIsMobile } from '../../hooks';
import { getMotifSchemeForLegacyBackgroundColor } from '../../motif';
import { BlockBoundary } from '../BlockBoundary';
import type { CardStackCardProps } from './CardStackCard/types';
import {
  boundaryCss,
  cardsContainer2CardsCss,
  cardsContainerCss,
  cardStackBlockCss,
  cardStackBlockFullHeightCss,
  eyebrowCss,
  headerCss,
  mobileStackItemCss,
  mobileStickyStackItemCss,
  stickyCardsContainerCss,
  textAlignCenterCss,
  textAlignCenterMobileCss,
  textAlignEndCss,
  textAlignEndMobileCss,
  textAlignStartCss,
  textAlignStartMobileCss,
  textCss,
  titleCss,
} from './styles';
import type { CardStackBlockProps } from './types';
import { useCardStackScroll } from './useCardStackScroll';

export const CardStackBlock: FC<PropsWithChildren<CardStackBlockProps>> = ({
  anchorId,
  backgroundColor,
  children,
  eyebrow,
  fullHeight = false,
  text,
  textAlignmentDesktop = Alignment.Start,
  textAlignmentMobile = Alignment.Center,
  title,
}) => {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLUListElement>(null);
  const cardRefs = useRef<Array<HTMLLIElement | null>>([]);

  const hasHeader = !!eyebrow || !!title || !!text;
  const cardCount = Children.count(children);

  const setCardRef = (el: HTMLLIElement | null, index: number) => {
    cardRefs.current[index] = el;
  };

  const hasStickyCards = isMobile && cardCount > 2;

  useCardStackScroll({
    childCount: cardCount,
    cardRefs,
    containerRef,
    isEnabled: hasStickyCards,
  });

  const enhancedChildren = Children.map(children, (child, index) => {
    if (isValidElement<CardStackCardProps>(child)) {
      return (
        <li
          // biome-ignore lint/suspicious/noArrayIndexKey: Ok to use index as key.
          key={index}
          ref={el => setCardRef(el, index)}
          className={cx(mobileStackItemCss, { [mobileStickyStackItemCss]: hasStickyCards })}
          data-testid="sdsm-card-stack-card-wrapper"
        >
          {child}
        </li>
      );
    }

    return child;
  });

  return (
    <article
      id={anchorId}
      data-testid="sdsm-card-stack-block"
      className={cx(cardStackBlockCss, getMotifSchemeForLegacyBackgroundColor(backgroundColor), {
        [cardStackBlockFullHeightCss]: fullHeight,
      })}
    >
      <BlockBoundary className={boundaryCss}>
        {hasHeader && (
          <header
            data-testid="sdsm-card-stack-block-header"
            className={cx(headerCss, {
              // Desktop alignment
              [textAlignStartCss]: textAlignmentDesktop === Alignment.Start,
              [textAlignCenterCss]: textAlignmentDesktop === Alignment.Center,
              [textAlignEndCss]: textAlignmentDesktop === Alignment.End,
              // Mobile alignment
              [textAlignStartMobileCss]: textAlignmentMobile === Alignment.Start,
              [textAlignCenterMobileCss]: textAlignmentMobile === Alignment.Center,
              [textAlignEndMobileCss]: textAlignmentMobile === Alignment.End,
            })}
          >
            {eyebrow && <div className={eyebrowCss}>{eyebrow}</div>}
            {title && <h2 className={titleCss}>{title}</h2>}
            {text && <p className={textCss}>{text}</p>}
          </header>
        )}
        <ul
          ref={containerRef}
          className={cx(cardsContainerCss, {
            [cardsContainer2CardsCss]: cardCount === 2,
            [stickyCardsContainerCss]: hasStickyCards,
          })}
        >
          {enhancedChildren}
        </ul>
      </BlockBoundary>
    </article>
  );
};

CardStackBlock.displayName = 'CardStackBlock';
